import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { OperationSegmentSpec, WorkInstructionSpec } from '../../process-plan'
import {
  CollectionUtil,
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderStopEventPropagationDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { InstructionDetailComponent } from '../instruction-detail/instruction-detail.component'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { AsyncPipe } from '@angular/common'

@Component({
  selector: 'app-instruction-browser',
  templateUrl: './instruction-browser.component.html',
  styleUrls: ['./instruction-browser.component.scss'],
  imports: [
    MatIconButton,
    MatIcon,
    ElderTableComponent,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    ElderStopEventPropagationDirective,
    InstructionDetailComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
  ],
})
export class InstructionBrowserComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _operationSegmentSpec$ = new BehaviorSubject<OperationSegmentSpec>(null)

  private readonly _instructionSpecs$ = new BehaviorSubject<WorkInstructionSpec[]>([])

  private readonly _selectedInstructionSpec$ = new BehaviorSubject<WorkInstructionSpec>(null)

  private _creationMode: boolean

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  @Output()
  public save: EventEmitter<Event> = new EventEmitter<Event>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {}

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set operationSegmentSpec(op: OperationSegmentSpec) {
    this._operationSegmentSpec$.next(op)
    this.instructionSpecs = op.instructions
  }

  public get operationSegmentSpec$(): Observable<OperationSegmentSpec> {
    return this._operationSegmentSpec$.asObservable()
  }

  public get instructionSpecs$(): Observable<WorkInstructionSpec[]> {
    return this._instructionSpecs$.asObservable()
  }

  public set instructionSpecs(instructions: WorkInstructionSpec[]) {
    this._instructionSpecs$.next(instructions)
    this._operationSegmentSpec$.getValue().instructions = instructions
  }

  public get instructionSpecs(): WorkInstructionSpec[] {
    return this._instructionSpecs$.getValue()
  }

  public get selectedInstructionSpec$(): Observable<WorkInstructionSpec> {
    return this._selectedInstructionSpec$.asObservable()
  }

  public set selectedInstructionSpec(spec: WorkInstructionSpec) {
    this._selectedInstructionSpec$.next(spec)
  }

  public get selectedInstructionSpec(): WorkInstructionSpec {
    return this._selectedInstructionSpec$.getValue()
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onNewInstructionSpec($event: any): void {
    const newInstruction = new WorkInstructionSpec()
    newInstruction.taskName = 'Neue Anleitung'
    const specs = this.instructionSpecs
    this.instructionSpecs = [...specs, newInstruction]
  }

  public onRemoveInstructionSpecs(instructions: WorkInstructionSpec[]): void {
    this.instructionSpecs = this.instructionSpecs.filter((s) => !instructions.includes(s))
    this.selectedInstructionSpec = null
    this.onSave(null)
  }

  public onSave(event: Event): void {
    this.emitSave(event)
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public moveUp(instruction: WorkInstructionSpec): void {
    const specs = [...this.instructionSpecs]
    CollectionUtil.moveUp(specs, instruction)
    this.instructionSpecs = specs
  }

  public moveDown(instruction: WorkInstructionSpec): void {
    const specs = [...this.instructionSpecs]
    CollectionUtil.moveDown(specs, instruction)
    this.instructionSpecs = specs
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private emitSave(event?: Event): void {
    this.save.emit(event)
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }
}
